import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Check, Loader2, ExternalLink, Building, Globe, Linkedin, MapPin, TrendingUp } from "lucide-react";
import { RefreshIntelButton } from "./LeadCardParts";

const FitReasons = ({ reasons }) =>
  reasons.length > 0 && (
    <div className="mb-4">
      <h4 className="text-xs font-medium text-ism-teal uppercase tracking-wider mb-2">Why They're a Good Fit</h4>
      <ul className="space-y-1.5">
        {reasons.map((reason) => (
          <li key={typeof reason === "object" ? reason.text || JSON.stringify(reason) : reason} className="text-sm text-gray-300 flex items-start gap-2">
            <span className="text-ism-teal mt-0.5">•</span>
            <span>{typeof reason === "object" ? reason.text || JSON.stringify(reason) : String(reason)}</span>
          </li>
        ))}
      </ul>
    </div>
  );

const SourceLink = ({ url, label, color }) =>
  url && (
    <a href={url} target="_blank" rel="noopener noreferrer" className={`text-xs ${color} hover:underline flex items-center gap-1 mt-1`}>
      <ExternalLink className="h-3 w-3" />
      {label}
    </a>
  );

const BusinessIntel = ({ items, lastRefreshed, isRefreshing, onRefresh }) => {
  if (items.length === 0) {
    return (
      <div className="mb-4 flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
        <p className="text-xs text-gray-500">No business intel available yet</p>
        <RefreshIntelButton onClick={onRefresh} isRefreshing={isRefreshing} />
      </div>
    );
  }
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1">
          <TrendingUp className="h-3 w-3" /> Business Intelligence
          {lastRefreshed && <span className="text-gray-500 font-normal normal-case ml-2">Updated {lastRefreshed}</span>}
        </h4>
        <RefreshIntelButton onClick={onRefresh} isRefreshing={isRefreshing} />
      </div>
      <ul className="space-y-2">
        {items.map((intel, i) => (
          <li key={typeof intel === "object" ? intel.url || intel.text || i : intel} className="text-sm text-gray-300 p-2 rounded bg-white/5">
            {typeof intel === "object" ? (
              <div>
                <p>{intel.text || intel.title}</p>
                <SourceLink url={intel.url} label={intel.source || "Source"} color="text-ism-teal" />
              </div>
            ) : (
              <span>{String(intel)}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const GrowthSignals = ({ signals }) =>
  signals.length > 0 && (
    <div className="mb-4">
      <h4 className="text-xs font-medium text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-1">
        <TrendingUp className="h-3 w-3" /> Growth Signals
      </h4>
      <ul className="space-y-2">
        {signals.map((signal, i) => (
          <li key={signal.url || signal.text || i} className="text-sm text-gray-300 p-2 rounded bg-amber-500/10 border border-amber-500/20">
            <p>{signal.text}</p>
            <SourceLink url={signal.url} label="View Source" color="text-amber-400" />
          </li>
        ))}
      </ul>
    </div>
  );

const QuickLinks = ({ view }) => (
  <div className="flex items-center gap-3 flex-wrap">
    {view.linkedinUrl && (
      <a href={view.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-ism-teal hover:underline flex items-center gap-1">
        <Linkedin className="h-3 w-3" /> LinkedIn
      </a>
    )}
    {view.website && (
      <a href={view.website.startsWith("http") ? view.website : `https://${view.website}`} target="_blank" rel="noopener noreferrer"
        className="text-xs text-ism-teal hover:underline flex items-center gap-1">
        <Globe className="h-3 w-3" /> Website
      </a>
    )}
    {view.location && <span className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> {view.location}</span>}
    {view.industry && <span className="text-xs text-gray-500">{view.industry}</span>}
  </div>
);

const ConvertButton = ({ isConverting, isConverted, onClick }) => (
  <Button onClick={(e) => { e.stopPropagation(); onClick(); }} disabled={isConverting || isConverted} size="sm"
    className={isConverted
      ? "bg-green-500/20 text-green-400 border border-green-500/30 text-xs h-7"
      : "bg-ism-teal/20 text-ism-teal border border-ism-teal/30 hover:bg-ism-teal/30 text-xs h-7"}
    data-testid="convert-to-account-btn">
    {isConverting ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Converting...</>
      : isConverted ? <><Check className="h-3 w-3 mr-1" /> Account Created</>
      : <><Building className="h-3 w-3 mr-1" /> Convert to Account</>}
  </Button>
);

// "More Details" section of the full lead card.
export const LeadExpandedDetails = ({ lead, view, actions }) => (
  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
    transition={{ duration: 0.2 }} className="mt-3 pt-3 border-t border-white/10">
    <FitReasons reasons={view.fitReasons} />
    <BusinessIntel items={view.intelItems} lastRefreshed={actions.lastRefreshed} isRefreshing={actions.isRefreshing} onRefresh={actions.refreshIntel} />
    <GrowthSignals signals={view.signals} />
    {view.expansionAngle && (
      <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
        <h4 className="text-xs font-medium text-blue-400 uppercase tracking-wider mb-1">Expansion Opportunity</h4>
        <p className="text-sm text-gray-300">{view.expansionAngle}</p>
      </div>
    )}
    <div className="flex items-center justify-between flex-wrap gap-2">
      <QuickLinks view={view} />
      {lead.leadId && <ConvertButton isConverting={actions.isConverting} isConverted={actions.isConverted} onClick={actions.convertToAccount} />}
    </div>
  </motion.div>
);
